import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
    IconButton,
    Tooltip,
    CircularProgress,
    TextField,
    Button,
    Paper,
    Box,
    Divider,
} from "@mui/material";
import {
    AddOutlined,
    DeleteOutlined,
    EditOutlined,
    CheckOutlined,
    CloseOutlined,
    VisibilityOutlined,
    VisibilityOffOutlined,
} from "@mui/icons-material";
import {
    useCreateCredentialsMutation,
    useDeleteCredentialMutation,
    useListCredentialsQuery,
} from "store/services/eBillProviderApi";
import { toast } from "react-toastify";
import { useAuth } from "hooks/useAuth";
import { confirmToast } from "utils/confirmToast";

// ─── Masked Value ─────────────────────────────────────────────────────────────

const MaskedValue = ({ value }) => {
    const [visible, setVisible] = useState(false);
    return (
        <div className="flex items-center gap-2">
            <span className="font-mono text-sm tracking-widest">
                {visible ? value : "•".repeat(Math.min(value?.length ?? 8, 16))}
            </span>
            <IconButton size="small" onClick={() => setVisible((v) => !v)}>
                {visible ? (
                    <VisibilityOffOutlined fontSize="small" />
                ) : (
                    <VisibilityOutlined fontSize="small" />
                )}
            </IconButton>
        </div>
    );
};

// ─── Credential Row ───────────────────────────────────────────────────────────

const CredentialRow = ({
    cred,
    onSave,
    onDelete,
    isSaving,
    showValue = true,
    showDelete = true,
    allowEditKey = true,
}) => {
    const [editing, setEditing] = useState(false);
    const [editKey, setEditKey] = useState(cred.key);
    const [editValue, setEditValue] = useState(cred.value);

    const handleConfirm = () => {
        onSave({
            key: allowEditKey ? editKey : cred.key,
            value: showValue ? editValue : cred.value ?? "",
            originalKey: cred.key,
        });
        setEditing(false);
    };

    const handleCancel = () => {
        setEditKey(cred.key);
        setEditValue(cred.value);
        setEditing(false);
    };

    if (editing) {
        return (
            <Paper
                elevation={0}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    borderRadius: 2,
                }}
            >
                <TextField
                    size="small"
                    label="Key"
                    value={editKey}
                    onChange={(e) => setEditKey(e.target.value)}
                    sx={{ flex: 1 }}
                    disabled={!allowEditKey}
                />
                {showValue && (
                    <TextField
                        size="small"
                        label="Value"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        sx={{ flex: 2 }}
                    />
                )}
                <Tooltip title="Save">
                    <IconButton
                        size="small"
                        onClick={handleConfirm}
                        disabled={isSaving || !editKey}
                        sx={{ color: "#22c55e" }}
                    >
                        {isSaving ? (
                            <CircularProgress size={16} />
                        ) : (
                            <CheckOutlined fontSize="small" />
                        )}
                    </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                    <IconButton
                        size="small"
                        onClick={handleCancel}
                        sx={{ color: "#ef4444" }}
                    >
                        <CloseOutlined fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Paper>
        );
    }

    return (
        <Paper
            elevation={0}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                borderRadius: 2,
                transition: "border-color .2s",
                "&:hover": { borderColor: "primary.light" },
            }}
        >
            <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-xs font-semibold uppercase tracking-wider">
                    {cred.key}
                </span>
                {showValue && <MaskedValue value={cred.value} />}
            </div>
            <div className="flex shrink-0 items-center gap-1">
                <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => setEditing(true)}>
                        <EditOutlined fontSize="small" />
                    </IconButton>
                </Tooltip>
                {showDelete && (
                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            onClick={() => onDelete(cred.key)}
                            sx={{ color: "#ef4444" }}
                        >
                            <DeleteOutlined fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </div>
        </Paper>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const EBillProviderCredentialsTab = ({
    providerId,
    entityId,
    mode = "provider",
} = {}) => {
    const urlId = useParams()?.id;
    const id = providerId || urlId;
    const isEntityMode = mode === "entity";
    const { viewEntity, loggedInUser } = useAuth();

    const selectedEntityId =
        loggedInUser?.selectedEntity?.entity?._id ||
        loggedInUser?.selectedEntity?.entity ||
        null;

    const resolvedEntityId =
        entityId || viewEntity?.entity?._id || selectedEntityId || null;

    const [entityContextId, setEntityContextId] = useState(
        isEntityMode ? entityId : resolvedEntityId
    );
    const [fields, setFields] = useState([{ key: "", value: "" }]);
    const [pendingValues, setPendingValues] = useState({});
    const [showAddForm, setShowAddForm] = useState(false); // ← toggle

    React.useEffect(() => {
        if (!isEntityMode && !entityContextId && resolvedEntityId) {
            setEntityContextId(resolvedEntityId);
        }
    }, [isEntityMode, entityContextId, resolvedEntityId]);

    const effectiveEntityId = isEntityMode ? entityId : entityContextId;

    const credentialsQueryArg = effectiveEntityId
        ? { id, entityId: effectiveEntityId }
        : id;
    const shouldSkip = !id || !effectiveEntityId;

    const {
        data: credentialsData,
        isLoading: loadingCreds,
        refetch,
    } = useListCredentialsQuery(credentialsQueryArg, { skip: shouldSkip });

    const [createCredentials, { isLoading: saving }] =
        useCreateCredentialsMutation();
    const [deleteCredential] = useDeleteCredentialMutation();

    const credentials = credentialsData?.data ?? [];
    const savedCreds = credentials.filter(
        (c) => String(c?.value || "").trim().length > 0
    );
    const pendingCreds = credentials.filter(
        (c) => String(c?.value || "").trim().length === 0
    );
    const pendingKeysSignature = pendingCreds.map((c) => c?.key).join("|");

    React.useEffect(() => {
        if (!isEntityMode) return;
        setPendingValues((prev) => {
            const next = {};
            pendingCreds.forEach((cred) => {
                const key = String(cred?.key || "").trim();
                if (!key) return;
                next[key] = prev[key] ?? "";
            });
            return next;
        });
    }, [isEntityMode, pendingKeysSignature]);

    // ─── Form Helpers ─────────────────────────────────────────────────────────

    const handleFieldChange = (index, field, val) =>
        setFields((prev) =>
            prev.map((f, i) => (i === index ? { ...f, [field]: val } : f))
        );

    const addField = () =>
        setFields((prev) => [...prev, { key: "", value: "" }]);

    const removeField = (index) =>
        setFields((prev) => prev.filter((_, i) => i !== index));

    const handleCancelAddForm = () => {
        setShowAddForm(false);
        setFields([{ key: "", value: "" }]);
    };

    // Handlers

    const handleSaveNew = async () => {
        const valid = fields.filter((f) => f.key.trim());
        if (!valid.length) return;

        const confirmed = await confirmToast("Are you sure you want to save these credentials?");
        if (!confirmed) return;
        try {
            if (!effectiveEntityId) {
                toast.error("Entity context missing. Please select an entity.");
                return;
            }
            await createCredentials({
                id,
                credentials: valid,
                ...(effectiveEntityId ? { entityId: effectiveEntityId } : {}),
            }).unwrap();
            toast.success("Credentials saved successfully");
            setFields([{ key: "", value: "" }]);
            setShowAddForm(false);
            refetch();
        } catch (err) {
            toast.error(err?.data?.message || "Failed to save credentials");
        }
    };

    const handleUpdateCredential = async ({ key, value, originalKey }) => {
        if (!window.confirm(`Are you sure you want to update "${key}"?`)) return;
        try {
            if (!effectiveEntityId) {
                toast.error("Entity context missing. Please select an entity.");
                return;
            }
            if (originalKey && originalKey !== key) {
                await deleteCredential({
                    id,
                    key: originalKey,
                    ...(effectiveEntityId ? { entityId: effectiveEntityId } : {}),
                }).unwrap();
            }
            await createCredentials({
                id,
                credentials: [{ key, value }],
                ...(effectiveEntityId ? { entityId: effectiveEntityId } : {}),
            }).unwrap();
            toast.success("Credential updated successfully");
            refetch();
        } catch (err) {
            toast.error(err?.data?.message || "Failed to update credential");
        }
    };

    const handleDeleteCredential = async (keyToDelete) => {
        if (!window.confirm(`Are you sure you want to delete "${keyToDelete}"?`))
            return;
        try {
            if (!effectiveEntityId) {
                toast.error("Entity context missing. Please select an entity.");
                return;
            }
            await deleteCredential({
                id,
                key: keyToDelete,
                ...(effectiveEntityId ? { entityId: effectiveEntityId } : {}),
            }).unwrap();
            toast.success("Credential deleted successfully");
            refetch();
        } catch (err) {
            toast.error(err?.data?.message || "Failed to delete credential");
        }
    };

    const handleSavePendingValues = async () => {
        const payload = Object.entries(pendingValues || {})
            .filter(([, value]) => String(value || "").trim())
            .map(([key, value]) => ({ key, value }));
        if (!payload.length) return;
        if (!window.confirm("Save credential values?")) return;
        try {
            if (!effectiveEntityId) {
                toast.error("Entity context missing. Please select an entity.");
                return;
            }
            await createCredentials({
                id,
                credentials: payload,
                ...(effectiveEntityId ? { entityId: effectiveEntityId } : {}),
            }).unwrap();
            toast.success("Credentials saved successfully");
            setPendingValues({});
            refetch();
        } catch (err) {
            toast.error(err?.data?.message || "Failed to save credentials");
        }
    };

    const emptyListMessage = isEntityMode
        ? "No credentials saved yet."
        : "No credentials added yet.";

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="space-y-8 p-6">
            {/* ── Provider Mode ── */}
            {!isEntityMode ? (
                <Box
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: "background.paper",
                    }}
                >
                    {!showAddForm ? (
                        // Collapsed — just header + button
                        <div className="flex items-center justify-between">
                            <h3 className="text-xs font-bold uppercase tracking-widest text-[#94a3b8]">
                                Credential Keys
                            </h3>
                            <Button
                                startIcon={<AddOutlined />}
                                variant="contained"
                                size="small"
                                onClick={() => setShowAddForm(true)}
                            >
                                Add Credential
                            </Button>
                        </div>
                    ) : (
                        // Expanded — full form
                        <>
                            <div className="mb-4 flex items-center justify-between">
                                <h3 className="text-xs font-bold uppercase tracking-widest text-[#94a3b8]">
                                    Add Credential Keys
                                </h3>
                                <IconButton
                                    size="small"
                                    onClick={handleCancelAddForm}
                                    sx={{ color: "#ef4444" }}
                                >
                                    <CloseOutlined fontSize="small" />
                                </IconButton>
                            </div>

                            <div className="space-y-3">
                                {fields.map((field, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <TextField
                                            size="small"
                                            label="Key"
                                            placeholder="e.g. API_KEY"
                                            value={field.key}
                                            onChange={(e) =>
                                                handleFieldChange(index, "key", e.target.value)
                                            }
                                            sx={{ flex: 1 }}
                                        />
                                        {fields.length > 1 && (
                                            <IconButton
                                                size="small"
                                                onClick={() => removeField(index)}
                                                sx={{ color: "#ef4444" }}
                                            >
                                                <DeleteOutlined fontSize="small" />
                                            </IconButton>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="mt-4 flex items-center justify-between gap-3">
                                <Button
                                    startIcon={<AddOutlined />}
                                    variant="text"
                                    size="small"
                                    onClick={addField}
                                >
                                    Add Another Key
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveNew}
                                    disabled={saving || !fields.some((f) => f.key.trim())}
                                    sx={{ minWidth: 160 }}
                                >
                                    {saving ? (
                                        <CircularProgress size={18} sx={{ color: "white" }} />
                                    ) : (
                                        "Save Keys"
                                    )}
                                </Button>
                            </div>
                        </>
                    )}
                </Box>
            ) : (
                // ── Entity Mode ──
                <Box
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: "background.paper",
                    }}
                >
                    <h3 className="mb-4 text-xs font-bold uppercase tracking-widest text-[#94a3b8]">
                        Set Credential Values
                    </h3>
                    {pendingCreds.length === 0 ? (
                        <p className="py-2 text-sm text-[#64748b]">
                            No pending credentials.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {pendingCreds.map((cred) => (
                                <div key={cred.key} className="flex items-center gap-3">
                                    <TextField
                                        size="small"
                                        label="Key"
                                        value={cred.key}
                                        disabled
                                        sx={{ flex: 1 }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Value"
                                        placeholder="Enter value"
                                        value={pendingValues[cred.key] || ""}
                                        onChange={(e) =>
                                            setPendingValues((prev) => ({
                                                ...prev,
                                                [cred.key]: e.target.value,
                                            }))
                                        }
                                        sx={{ flex: 2 }}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="mt-4 flex items-center justify-end gap-3">
                        <Button
                            variant="contained"
                            onClick={handleSavePendingValues}
                            disabled={
                                saving ||
                                !Object.values(pendingValues || {}).some((v) =>
                                    String(v || "").trim()
                                )
                            }
                            sx={{ minWidth: 160 }}
                        >
                            {saving ? (
                                <CircularProgress size={18} sx={{ color: "white" }} />
                            ) : (
                                "Save Credentials"
                            )}
                        </Button>
                    </div>
                </Box>
            )}

            <Divider sx={{ borderBottomWidth: 4, my: 4 }} />

            {/* ── Listed Credentials ── */}
            <div>
                <h3 className="mb-4 text-xs font-bold uppercase tracking-widest text-[#94a3b8]">
                    Listed Credentials
                </h3>
                {loadingCreds ? (
                    <div className="flex justify-center py-8">
                        <CircularProgress size={28} />
                    </div>
                ) : (isEntityMode ? savedCreds : credentials).length === 0 ? (
                    <p className="py-4 text-sm text-[#64748b]">{emptyListMessage}</p>
                ) : (
                    <div className="space-y-2">
                        {(isEntityMode ? savedCreds : credentials).map((cred) => (
                            <CredentialRow
                                key={cred.key}
                                cred={cred}
                                onSave={handleUpdateCredential}
                                onDelete={handleDeleteCredential}
                                isSaving={saving}
                                showValue={isEntityMode}
                                showDelete={!isEntityMode}
                                allowEditKey={!isEntityMode}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EBillProviderCredentialsTab;